import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadCloud, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet, ChevronRight, ChevronDown } from "lucide-react";

const Cell = ({ label, value, tone = "slate" }) => (
  <div className={`rounded-lg border p-2 text-center ${tone === "red" ? "border-[#B01E28] bg-[#FCF1F1]" : tone === "amber" ? "border-amber-400 bg-amber-50" : tone === "green" ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
    <div className={`font-mono font-bold text-lg ${tone === "red" ? "text-[#B01E28]" : tone === "amber" ? "text-amber-700" : tone === "green" ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
  </div>
);

export const LocationImportDialog = ({ open, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [openTree, setOpenTree] = useState({});

  const reset = () => { setFile(null); setPreview(null); setOpenTree({}); };
  const close = () => { reset(); onClose(); };

  const doPreview = async (f) => {
    setBusy(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/locations/import/preview", fd);
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); doPreview(f);
  };

  const doCommit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/locations/import/commit", fd);
      toast.success(`Imported ${data.created.total} locations: ${data.created.spots} spots across ${data.created.structures} structures.`);
      onDone?.(); close();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  const s = preview?.summary;
  const badRows = (preview?.rows || []).filter((r) => r.status !== "ok");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="location-import-dialog" className="max-w-3xl max-h-kb overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#B01E28]" />Import locations from spreadsheet</DialogTitle>
        </DialogHeader>

        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
          Upload your <b>.xlsx</b> with an <b>“All Locations”</b> tab (columns: Location Code, Site, Zone, Structure, Bay, Level, Level Type, Description, Active).
          Your <b>Location Code is preserved exactly</b> as each location’s code. This is a <b>dry-run preview first</b> — nothing is saved until you press <b>Commit</b>.
        </div>

        <label data-testid="import-file-drop" className="mt-3 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl p-6 cursor-pointer hover:border-[#E0781A] transition-colors">
          <UploadCloud className="w-7 h-7 text-slate-400" />
          <span className="text-sm text-slate-600">{file ? file.name : "Click to choose your .xlsx file"}</span>
          <input data-testid="import-file-input" type="file" accept=".xlsx" className="hidden" onChange={onPick} />
        </label>

        {busy && <div className="flex items-center gap-2 text-sm text-slate-500 mt-4"><Loader2 className="w-4 h-4 animate-spin" />Reading & validating…</div>}

        {s && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-4 gap-2">
              <Cell label="Rows read" value={s.total_rows} />
              <Cell label="Will create" value={s.create_total} tone="green" />
              <Cell label="Errors" value={s.errors} tone={s.errors ? "red" : "slate"} />
              <Cell label="Warnings" value={s.warnings} tone={s.warnings ? "amber" : "slate"} />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <Cell label="Sites" value={s.create_sites} />
              <Cell label="Zones" value={s.create_zones} />
              <Cell label="Structures" value={s.create_structures} />
              <Cell label="Spots" value={s.create_spots} />
            </div>

            {s.errors > 0
              ? <div data-testid="import-blocked" className="flex items-start gap-2 text-sm border border-[#B01E28] bg-[#FCF1F1] text-[#7F1419] rounded-lg p-3"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />Fix the {s.errors} error row(s) in your spreadsheet, then re-upload. Nothing can be imported until they’re resolved.</div>
              : <div data-testid="import-ready" className="flex items-start gap-2 text-sm border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-lg p-3"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />No errors. Review the tree below, then Commit to create these locations.</div>}

            {badRows.length > 0 && (
              <div data-testid="import-issues" className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Rows needing attention ({badRows.length})</div>
                <div className="max-h-40 overflow-y-auto divide-y divide-slate-100">
                  {badRows.map((r) => (
                    <div key={r.row} className="flex items-start gap-2 px-3 py-1.5 text-xs">
                      <span className="font-mono text-slate-400 w-10 shrink-0">#{r.row}</span>
                      {r.status === "error" ? <AlertTriangle className="w-3.5 h-3.5 text-[#B01E28] mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />}
                      <span className="font-mono text-slate-700 shrink-0">{r.code || "(no code)"}</span>
                      <span className="text-slate-500">{r.messages.join("; ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* tree preview */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Tree to be created</div>
              <div className="max-h-72 overflow-y-auto p-2 text-sm">
                {(preview.tree || []).map((site) => (
                  <div key={site.code}>
                    <div className="font-bold flex items-center gap-1.5 py-0.5"><span className="font-mono text-[#B01E28]">{site.code}</span>{site.name}</div>
                    {site.zones.map((z) => (
                      <div key={z.code} className="ml-3">
                        <div className="flex items-center gap-1.5 py-0.5 text-slate-700"><span className="font-mono text-xs text-slate-400">{z.code}</span>{z.name}</div>
                        {z.structures.map((st) => {
                          const k = st.code; const isOpen = openTree[k];
                          return (
                            <div key={k} className="ml-4">
                              <button data-testid={`tree-struct-${st.code}`} onClick={() => setOpenTree((o) => ({ ...o, [k]: !o[k] }))} className="flex items-center gap-1 py-0.5 text-slate-700 hover:text-[#B01E28]">
                                {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                <span className="font-mono text-xs text-slate-400">{st.code}</span>{st.name}
                                <span className="text-[10px] text-slate-400">· {st.spots.length} spots</span>
                              </button>
                              {isOpen && (
                                <div className="ml-6 flex flex-wrap gap-1 py-1">
                                  {st.spots.map((sp) => (
                                    <span key={sp.code} title={sp.name} className="font-mono text-[10px] bg-slate-100 rounded px-1.5 py-0.5 text-slate-600">{sp.code}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" className="rounded-lg" onClick={close} disabled={committing}>Cancel</Button>
          <Button data-testid="import-commit-button" onClick={doCommit} disabled={!s || !s.can_commit || committing || busy} className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white disabled:opacity-40">
            {committing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            {s ? `Commit — create ${s.create_total} locations` : "Commit import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
